
import React, { useEffect, useState } from "react";
import axios from "axios";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import { encryptPayload, decryptResponse } from "./CryptoUtil";
import { api } from "../App";

const ROLE_CONFIG = {
  ADMIN: {
    title: "Admin Login",
    placeholder: "AD1001 / email / phone",
    dashboard: "/admin/dashboard",
    idKey: "admin_id",
  },
  ZSM: {
    title: "ZSM Login",
    placeholder: "ZSM1001 / email / phone",
    dashboard: "/zsm/dashboard",
    idKey: "zsm_id",
  },
  ASM: {
    title: "ASM Login",
    placeholder: "ASM1001 / email / phone",
    dashboard: "/asm/dashboard",
    idKey: "asm_id",
  },
  SM: {
    title: "SM Login",
    placeholder: "SM1001 / email / phone",
    dashboard: "/sm/dashboard",
    idKey: "sm_id",
  },
};

const STORAGE_KEYS = [
  "admin_id",
  "zsm_id",
  "asm_id",
  "sm_id",
  "role",
];

const getRoleFromPath = (pathname) => {
  const path = pathname.toLowerCase().replace(/\/+$/, "");

  if (path.endsWith("/admin/login")) return "ADMIN";
  if (path.endsWith("/zsm/login")) return "ZSM";
  if (path.endsWith("/asm/login")) return "ASM";
  if (path.endsWith("/sm/login")) return "SM";

  return "";
};

const getErrorMessage = (data, status) => {
  // Some backend errors may be returned without encryption.
  if (data?.message) return data.message;
  if (data?.error) return data.error;

  // Decrypt the standard encrypted API error response.
  if (data?.encrypted?.payload && data?.encrypted?.transactionId) {
    try {
      const result = decryptResponse(
        data.encrypted.payload,
        data.encrypted.transactionId
      );

      if (result?.message) return result.message;
      if (result?.error) return result.error;
    } catch (error) {
      console.error("Could not decrypt login error.");
    }
  }

  const messages = {
    400: "Invalid request.",
    401: "Invalid login credentials.",
    403: "You are not authorized to access this panel.",
    404: "Login API not found. Check the API URL.",
    422: "Please enter valid login details.",
    500: "Server error. Please check the PHP backend logs.",
  };

  return messages[status] || "Unable to log in. Please try again.";
};

export default function Login() {
  const location = useLocation();
  const navigate = useNavigate();

  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [expectedRole, setExpectedRole] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const role = getRoleFromPath(location.pathname);

    setExpectedRole(role);
    setLogin("");
    setPassword("");
    setMsg("");
    setShowPassword(false);
  }, [location.pathname]);

  const currentConfig = ROLE_CONFIG[expectedRole];

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (loading) return;

    const role = getRoleFromPath(location.pathname);

    if (!role || !ROLE_CONFIG[role]) {
      setMsg("Invalid login page. Open the correct role login URL.");
      return;
    }

    const cleanLogin = login.trim();

    if (!cleanLogin || !password) {
      setMsg("Please enter your login ID and password.");
      return;
    }

    setLoading(true);
    setMsg("");

    try {
      /*
       * IMPORTANT:
       * Clear previous role IDs so another user's old session
       * cannot be mistaken for the current login.
       */
      STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));

      /*
       * Encrypt credentials using the existing CryptoUtil.js.
       * Do not replace or modify CryptoUtil.js for this flow.
       */
      const encryptedRequest = encryptPayload({
        login: cleanLogin,
        password,
        role,
      });

      if (!encryptedRequest) {
        throw new Error("Unable to encrypt the login request.");
      }

      const endpoint = `${String(api).replace(/\/+$/, "")}/login.php`;

      const response = await axios.post(
        endpoint,
        encryptedRequest,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          withCredentials: true,
          timeout: 20000,
        }
      );

      const responseData = response.data;

      /*
       * Support the existing encrypted response format:
       * {
       *   success: true,
       *   encrypted: {
       *     payload: "...",
       *     transactionId: "..."
       *   }
       * }
       */
      let result = responseData;

      if (
        responseData?.encrypted?.payload &&
        responseData?.encrypted?.transactionId
      ) {
        result = decryptResponse(
          responseData.encrypted.payload,
          responseData.encrypted.transactionId
        );
      }

      if (!result || result.success !== true || !result.user) {
        throw new Error(
          result?.message || "Login failed. Please try again."
        );
      }

      const serverRole = String(result.user.role || "")
        .trim()
        .toUpperCase();

      /*
       * Never trust a redirect or role that does not match
       * the role selected by the current login URL.
       */
      if (serverRole !== role) {
        throw new Error("The server returned an invalid user role.");
      }

     
      const config = ROLE_CONFIG[serverRole];
      const ids = result.ids || result.user.ids || {};

      // IDs are strings, e.g. AD1001, ZSM1001, ASM1001, SM1001.
      const currentUserId = String(
        result.user?.id ?? result.user?.code ?? ""
      ).trim();

      const expectedIdPattern = {
        ADMIN: /^AD\d+$/i,
        ZSM: /^ZSM\d+$/i,
        ASM: /^ASM\d+$/i,
        SM: /^SM\d+$/i,
      };

      if (!expectedIdPattern[serverRole]?.test(currentUserId)) {
        throw new Error("Invalid user ID received from the server.");
      }

      const storageValues = {
        admin_id: ids.admin_id ?? null,
        zsm_id: ids.zsm_id ?? null,
        asm_id: ids.asm_id ?? null,
        sm_id: ids.sm_id ?? null,
      };

      // Ensure the logged-in role always has its own ID.
      storageValues[config.idKey] = currentUserId;

      // Validate and save all available hierarchy IDs.
      Object.entries(storageValues).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          const id = String(value).trim();

          if (!/^(AD|ZSM|ASM|SM)\d+$/i.test(id)) {
            throw new Error(`Invalid ${key} received from the server.`);
          }

          localStorage.setItem(key, id);
        } else {
          localStorage.removeItem(key);
        }
      });

      localStorage.setItem("role", serverRole);

      // IMPORTANT: App.js route guards require userSession.
      const sessionData = {
        user: {
          id: currentUserId,
          code: currentUserId,
          name: result.user?.name ?? "",
          email: result.user?.email ?? "",
          phone: result.user?.phone ?? "",
          role: serverRole,
          userlevel: serverRole,

          admin_id: storageValues.admin_id,
          zsm_id: storageValues.zsm_id,
          asm_id: storageValues.asm_id,
          sm_id: storageValues.sm_id,
        },
        token: result.token,
        expiry: result.expiry ?? null,
      };

      if (!sessionData.token) {
        throw new Error("Authentication token missing from the server.");
      }

      localStorage.setItem("userSession", JSON.stringify(sessionData));

      // React Router navigates to the correct dashboard.
      navigate(config.dashboard, { replace: true });

    } catch (error) {
      console.error(
        "Login failed:",
        error.response?.status || error.message
      );

      /*
       * Do not leave partial or stale identity data after failure.
       */
      STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));

      if (error.response) {
        setMsg(
          getErrorMessage(
            error.response.data,
            error.response.status
          )
        );
      } else if (error.code === "ECONNABORTED") {
        setMsg("Login request timed out. Please try again.");
      } else {
        setMsg(
          error.message || "Unable to connect to the login server."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-scroller">
      <div className="container-fluid page-body-wrapper">
        <div className="row w-100 mx-0">
          <div className="col-lg-4 mx-auto">
            <div className="auth-form-light text-left py-5 px-4 px-sm-5">

              <div className="brand-logo text-center mb-3">
                <h3>{currentConfig?.title || "Login"}</h3>
              </div>

              <h6 className="font-weight-light text-center">
                Sales Management System
              </h6>

              <form className="pt-3" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="mb-2">
                    {expectedRole
                      ? `${expectedRole} ID / Email / Phone`
                      : "ID / Email / Phone"}
                  </label>

                  <input
                    type="text"
                    className="form-control form-control-lg"
                    value={login}
                    onChange={(event) => {
                      setLogin(event.target.value);
                      if (msg) setMsg("");
                    }}
                    placeholder={
                      currentConfig?.placeholder || "ID / email / phone"
                    }
                    autoComplete="username"
                    disabled={loading}
                    required
                  />
                </div>

                <div className="form-group mt-3">
                  <label className="mb-2">Password</label>

                  <div className="input-group input-group-lg">
                    <input
                      type={showPassword ? "text" : "password"}
                      className="form-control"
                      placeholder="Enter Password"
                      value={password}
                      onChange={(event) => {
                        setPassword(event.target.value);
                        if (msg) setMsg("");
                      }}
                      autoComplete="current-password"
                      disabled={loading}
                      required
                    />

                    <button
                      type="button"
                      className="input-group-text bg-white"
                      onClick={() => setShowPassword((previous) => !previous)}
                      disabled={loading}
                      tabIndex={-1}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      style={{
                        cursor: loading ? "not-allowed" : "pointer",
                      }}
                    >
                      {showPassword ? (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20C5 20 1 12 1 12a20.6 20.6 0 0 1 5.06-6.06" />
                          <path d="M9.9 4.24A9.9 9.9 0 0 1 12 4c7 0 11 8 11 8a20.6 20.6 0 0 1-3.22 4.36" />
                          <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      ) : (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12Z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {msg && (
                  <div className="alert alert-danger mt-3" role="alert">
                    {msg}
                  </div>
                )}

                <div className="mt-4 d-grid">
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg"
                    disabled={loading || !expectedRole}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm mr-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Logging in...
                      </>
                    ) : (
                      `Login as ${expectedRole || "User"}`
                    )}
                  </button>
                </div>

                <div className="mt-3 text-center">
                  <NavLink
                    to={`/forgot-password?role=${expectedRole}`}
                  >
                    Forgot password?
                  </NavLink>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
